<template>
  <div class="p-tasklist-sticky">
    <transition>
      <div v-if="isActive" class="p-tasklist-sticky-form" :style="cssStyle">
        <div class="p-tasklist-sticky-form__header">
          <h2 class="p-tasklist-sticky-form__title">
            <Icon2 color="white" variant="task" :size="12" />タスク
          </h2>
          <transition name="v-fade">
            <Label v-if="taskCount" variant="count">{{ taskCount }}</Label>
          </transition>
          <router-link
            :to="{ name: 'taskList' }"
            class="p-tasklist-sticky-form__title_link"
          >
            <Icon2 color="white" variant="task_link" :size="12" />
          </router-link>
        </div>
        <div class="p-tasklist-sticky-form__inner">
          <div class="p-tasklist-sticky-form__content">
            <div class="c-fieldset c-fieldset--wrap">
              <div class="c-fieldset__header">
                <p class="c-fieldset__title">施策内容を選択する</p>
              </div>
              <div class="c-fieldset__content">
                <Select
                  v-model="params.type"
                  :options="types"
                  placeholder="タスクの施策内容を選択してください"
                  :selected="params.type"
                  size="sm"
                  required
                  data-testid="input-type"
                />
              </div>
            </div>
            <div class="c-fieldset c-fieldset--wrap">
              <div class="c-fieldset__header">
                <p class="c-fieldset__title">タスクに担当者を設定する</p>
              </div>
              <div class="c-fieldset__content c-alertPos">
                <Select
                  v-model="params.operator"
                  :options="projectUsers"
                  placeholder="担当者なし"
                  :selected="params.operator"
                  size="sm"
                  :class="{
                    alert: error.operator
                  }"
                />
                <Alert v-if="error.operator">
                  {{ error.operator }}
                </Alert>
              </div>
            </div>
            <div class="c-fieldset c-fieldset--wrap">
              <div class="c-fieldset__header">
                <p class="c-fieldset__title">
                  詳細<span class="c-required">必須</span>
                </p>
              </div>
              <div class="c-fieldset__content c-alertPos">
                <Textarea
                  v-model="params.text"
                  v-focus="isActive"
                  placeholder="タスクの詳細を記入してください"
                  size="sm"
                  :class="{
                    alert: isEdited.text && error.text
                  }"
                  data-testid="input-text"
                />
                <Alert v-if="isEdited.text && error.text">
                  {{ error.text }}
                </Alert>
              </div>
            </div>
            <template v-if="params.type">
              <div class="c-fieldset c-fieldset--wrap">
                <div class="c-fieldset__header">
                  <p class="c-fieldset__title">
                    施策を実施するキーワードを設定する<span
                      v-if="isChangeTd || isRewrite || isCreateContent"
                      class="c-required"
                      >必須</span
                    >
                  </p>
                </div>
                <div class="c-fieldset__content c-alertPos">
                  <Input
                    v-model="params.keyword"
                    placeholder="キーワードを入力してください"
                    size="sm"
                    :class="{
                      alert: isEdited.keyword && error.keyword
                    }"
                    @focusout="focusoutKeyword"
                    data-testid="input-keyword"
                  />
                  <Alert v-if="isEdited.keyword && error.keyword">
                    {{ error.keyword }}
                  </Alert>
                </div>
              </div>
              <div class="c-fieldset c-fieldset--wrap">
                <div class="c-fieldset__header">
                  <p class="c-fieldset__title">
                    施策を実施するページURLを登録する<span
                      v-if="isChangeTd || isRewrite || isSeoCheckUp"
                      class="c-required"
                      >必須</span
                    >
                  </p>
                </div>
                <div class="c-fieldset__content c-alertPos">
                  <InputUrl :url="project.siteUrl">
                    <Input
                      v-model="params.url"
                      placeholder="/"
                      size="sm"
                      :class="{
                        alert: isEdited.url && error.url
                      }"
                      @focusout="focusoutUrl"
                      data-testid="input-url"
                    />
                    <Alert v-if="isEdited.url && error.url">
                      {{ error.url }}
                    </Alert>
                  </InputUrl>
                </div>
              </div>
            </template>
            <div class="c-fieldset c-fieldset--wrap">
              <div class="c-fieldset__header">
                <p class="c-fieldset__title">施策完了予定日</p>
              </div>
              <div class="c-fieldset__content c-alertPos">
                <DatePicker v-model="params.executionDate" :max-date="null" />
                <Alert v-if="error.executionDate">
                  {{ error.executionDate }}
                </Alert>
              </div>
            </div>
          </div>
          <div class="p-tasklist-sticky-form__footer">
            <div class="c-alertPos">
              <Button
                variant="main"
                fsize="f14"
                icon="plus"
                :disabled="isDisabled"
                class="l-w-200 l-pr-2"
                :class="{ 'is-loading': isProcessing }"
                @click="submit"
                data-testid="button-submit"
              >
                タスクを登録する
              </Button>
              <Alert v-if="errorResponse">
                {{ errorResponse }}
              </Alert>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <Button
      variant="main"
      class="p-tasklist-sticky__btn"
      @click="toggle"
      data-testid="button-toggle"
    >
      <Icon2 v-if="isActive" key="close" variant="close" :size="16" />
      <template v-else>
        <Icon2 key="open" variant="board" :size="25" />
        タスク
      </template>
    </Button>
  </div>
</template>

<script>
import { ERROR_MESSAGE, TASK_LENGTHS_LIMIT } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import DatePicker from '@/components/DatePicker';
import Icon2 from '@/components/Icon2';
import Input from '@/components/Input';
import InputUrl from '@/components/InputUrl';
import Label from '@/components/Label';
import Select from '@/components/Select';
import Textarea from '@/components/Textarea';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Alert,
    Button,
    DatePicker,
    Icon2,
    Input,
    InputUrl,
    Label,
    Select,
    Textarea
  },
  data() {
    return {
      error: {
        keyword: '',
        operator: '',
        text: '',
        type: '',
        url: ''
      },
      errorResponse: '',
      params: {
        keyword: '',
        operator: '',
        text: this.$store.state.task.draft[this.project.id] || '',
        type: '',
        url: '',
        executionDate: null
      },
      isEdited: {
        keyword: false,
        operator: false,
        text: false,
        type: false,
        url: false
      },
      isProcessing: false
    };
  },
  computed: {
    cssStyle() {
      return {
        'max-height': 'calc(100vh - ' + (this.notificationsHeight + 115) + 'px)'
      };
    },
    notificationsHeight() {
      return this.$store.state.view.size.notificationsHeight;
    },
    projectUsers() {
      return this.$store.getters['project/usersSelect'];
    },
    taskCount() {
      return this.$store.state.project.task.count;
    },
    types() {
      return this.$store.getters['task/solutionSelect'];
    },
    isActive() {
      return this.$store.state.task.status.isActive;
    },
    isChangeTd() {
      return this.params.type === 'changeTd';
    },
    isCreateContent() {
      return this.params.type === 'createContent';
    },
    isRewrite() {
      return this.params.type === 'rewrite';
    },
    isSeoCheckUp() {
      return this.params.type === 'seoCheckUp';
    },
    isOther() {
      return this.params.type === 'other';
    },
    isDisabled() {
      return this.isError || this.isProcessing;
    },
    isError() {
      if (
        this.error.text ||
        this.error.type ||
        this.error.operator ||
        this.error.keyword ||
        this.error.url
      ) {
        return true;
      }
      return false;
    }
  },
  watch: {
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponse = '';
      },
      deep: true
    },
    'params.keyword': {
      handler(value, oldValue) {
        const keyword = value.trim();
        this.validateKeyword(keyword);
        this.isEdited.keyword = keyword ? true : false;
      },
      immediate: true
    },
    'params.operator': {
      handler(value) {
        const operator = value;
        this.error.operator = '';
        this.isEdited.operator = operator ? true : false;
      },
      immediate: true
    },
    'params.text': {
      handler(value) {
        const text = value.trim();
        this.error.text = new Validation()
          .input()
          .maxLength(TASK_LENGTHS_LIMIT)
          .test(text);
        this.isEdited.text = text ? true : false;
      },
      immediate: true
    },
    'params.type': {
      handler(value) {
        const type = value;
        this.error.type = new Validation().select().test(type);
        this.isEdited.type = type ? true : false;
        const keyword = this.params.keyword.trim();
        this.validateKeyword(keyword);
        const url = this.params.url.trim();
        this.validateUrl(url);
      },
      immediate: true
    },
    'params.url': {
      handler(value, oldValue) {
        const url = value.trim();
        this.validateUrl(url);
        this.isEdited.url = url ? true : false;
      },
      immediate: true
    },
    'project.id': {
      handler() {
        if (!this.project.id) {
          return;
        }
        this.resetForm();
        this.params.text = this.$store.state.task.draft[this.project.id] || '';
        this.$store.dispatch('project/getTaskCount');
        this.$store.dispatch('project/getUsers');
      },
      immediate: true
    }
  },
  created() {
    this.$store.dispatch('task/getTaskSolutions');
  },
  methods: {
    async submit() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.errorResponse = '';

      // 利用量チェック
      await this.getUsages();
      if (this.errorResponse) {
        this.isProcessing = false;
        return;
      }
      // ユーザーの存在チェック
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing = false;
          return;
        }
      }
      await this.postTask();

      this.isProcessing = false;
    },
    focusoutKeyword() {
      this.params.keyword = this.$options.filters.replaceDoubleSpaceToSpace(
        this.params.keyword.trim()
      );
    },
    focusoutUrl() {
      this.params.url = this.$options.filters.replaceDoubleSpaceToSpace(
        this.params.url
      );
      // 先頭が/じゃないURLが入力されていたら、スラッシュを付ける
      if (this.params.url && !/^\//.test(this.params.url)) {
        this.params.url = '/' + this.params.url;
      }
    },
    validateKeyword(value) {
      const keyword = value.trim();
      if (this.isChangeTd || this.isCreateContent || this.isRewrite) {
        this.error.keyword = new Validation()
          .input()
          .keyword()
          .test(keyword);
      } else if (this.isOther || this.isSeoCheckUp) {
        this.error.keyword = new Validation().keyword().test(keyword);
      }
    },
    validateUrl(url) {
      if (this.isChangeTd || this.isSeoCheckUp || this.isRewrite) {
        this.error.url = new Validation().input().test(url);
      } else {
        this.error.url = '';
      }
    },
    getFullUrl(url) {
      return (
        this.$options.filters.removeTrailingSlash(this.project.siteUrl) +
        '/' +
        this.$options.filters.removeLeadingSlash(url)
      );
    },
    getTaskParams() {
      const params = {
        column: 'todo',
        keyword: null,
        operatorUserId: this.params.operator || null,
        position: null,
        taskDetail: this.params.text,
        type: this.params.type,
        url: null,
        executionDate: this.params.executionDate
          ? utils.formatDate(this.params.executionDate, 'YYYY-MM-DD')
          : null
      };
      params.keyword = this.params.keyword ? this.params.keyword : null;
      params.url = this.params.url ? this.getFullUrl(this.params.url) : null;

      return params;
    },
    getUsages() {
      const params = {
        items: 'tasks'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          const usages = response.data.items[0];
          const error = utils.getErrorUsages({
            usages: usages,
            target: 'タスク'
          });
          if (error.message) {
            this.errorResponse = error.message;
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    getUsers() {
      return this.$store.dispatch('project/getUsers').finally(() => {
        const existUser = this.projectUsers.some(
          user => user.value === this.params.operator
        );
        if (!existUser) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        }
      });
    },
    postTask() {
      const params = this.getTaskParams();

      return apiProjects.tasks.siteAnalysis
        .post(params, this.auth.idToken, this.project.id)
        .then(response => {
          this.$store.dispatch('project/getTaskCount');
          this.resetForm();
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    resetForm() {
      this.params.keyword = '';
      this.params.operator = '';
      this.params.text = '';
      this.params.type = '';
      this.params.url = '';
      this.params.executionDate = null;
    },
    toggle() {
      // 制限文字数以上のテキストをlocalStorageに保存しないようにする
      const draft = this.params.text.slice(0, TASK_LENGTHS_LIMIT);
      if (draft) {
        this.$store.dispatch('task/setDraft', { [this.project.id]: draft });
      }
      this.$store.dispatch('task/setStatus', { isActive: !this.isActive });
    }
  }
};
</script>
